import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, Phone, Car, Bike, Gauge, Wrench, Search,
  ShieldCheck, Eye, FileCheck, Handshake, Star, MapPin, Wrench as WrenchIcon, BadgePercent, ArrowDown,
} from "lucide-react";
import { SITE, IMAGES, API_URL, toYoutubeEmbed } from "@/lib/site";
import { Reveal, MaskedLines, SectionHead } from "@/components/motion";
import SectionNav from "@/components/SectionNav";
import BeforeAfter from "@/components/BeforeAfter";
import { useSEO } from "@/components/SEO";

const MARQUEE_ITEMS = [
  "Mécanicien Prilly", "Garage indépendant Lausanne", "Réparation moto Vaud",
  "Entretien scooter Lausanne", "Devis déduit des travaux", "Transparence totale",
  "Ouest lausannois", "Auto · Moto · Scooter",
];

const SERVICES_HOME = [
  {
    icon: Car,
    title: "Automobile",
    desc: "Entretien, freinage, diagnostic électronique et mécanique générale pour toutes marques.",
    points: ["Vidange & révision", "Freins & pneus", "Diagnostic voyants"],
    image: IMAGES.vitrineAutoFace,
    testid: "service-card-auto",
  },
  {
    icon: Bike,
    title: "Moto",
    desc: "Entretien complet et mécanique deux-roues, de la révision à la recherche de panne.",
    points: ["Kit chaîne & pneus", "Freinage moto", "Préparation saison"],
    image: IMAGES.vitrineMoto,
    tag: "Kawasaki Z750 Black Saphir",
    testid: "service-card-moto",
  },
  {
    icon: Gauge,
    title: "Scooter",
    desc: "Révision et réparation de scooters pour vos trajets quotidiens Lausanne – Prilly.",
    points: ["Entretien périodique", "Transmission", "Freinage & pneus"],
    image: IMAGES.scooter,
    testid: "service-card-scooter",
  },
];

const TRANSPARENCE = [
  {
    num: "01", icon: FileCheck, title: "Devis avant travaux",
    text: "Chaque intervention commence par un devis clair. Aucun travail n'est facturé sans votre accord préalable.",
  },
  {
    num: "02", icon: Eye, title: "Zéro surprise",
    text: "Si un problème supplémentaire est découvert en cours de route, vous êtes appelé avant toute décision.",
  },
  {
    num: "03", icon: Search, title: "Preuves visuelles",
    text: "Les pièces remplacées vous sont montrées ou restituées sur demande. Vous savez exactement ce qui a été fait.",
  },
  {
    num: "04", icon: Handshake, title: "Explications simples",
    text: "Pas de jargon inutile : on vous explique l'état de votre véhicule et les priorités, en langage clair.",
  },
];

const POURQUOI = [
  { icon: Wrench, title: "Atelier indépendant", text: "Sans réseau ni franchise : vous parlez directement à la personne qui travaille sur votre véhicule." },
  { icon: ShieldCheck, title: "Travail soigné", text: "Minutie, soin et rigueur sur chaque intervention — votre véhicule est traité comme le nôtre." },
  { icon: MapPin, title: "Proche de chez vous", text: "À Prilly, à deux pas de Lausanne et Renens. Facile d'accès pour déposer et récupérer votre véhicule." },
  { icon: Phone, title: "Contact direct", text: "Un appel suffit. Pas de standard, pas d'attente : une réponse rapide et un devis clair, déduit des travaux." },
];

const OFFRES_HIVER = [
  {
    name: "Check-up Hiver",
    price: "CHF 59.-",
    detail:
      "9 points de contrôle essentiels avant la saison froide, dont : pneus (état, usure, pression), freins (contrôle visuel), batterie, éclairage (tous feux), liquides (refroidissement, lave-glace), essuie-glaces et niveaux (huile, etc.).",
    note: "Durée : 30 – 45 min",
    featured: false,
  },
  {
    name: "Pack Hiver",
    price: "CHF 99.-",
    detail: "Check-up hiver complet + permutation des 4 roues.",
    note: "Au lieu de CHF 109.- — vous économisez CHF 10.-",
    featured: true,
  },
  {
    name: "Pack Sécurité Hiver",
    price: "CHF 129.-",
    detail:
      "Check-up hiver complet + permutation des 4 roues + contrôle batterie, éclairage, lave-glace, essuie-glaces, freins, pneus et niveaux + contrôle spécifique de la visibilité. Remplacement des essuie-glaces : main-d'œuvre incluse (balais selon véhicule, en supplément).",
    note: null,
    featured: false,
  },
  {
    name: "Permutation des 4 roues",
    price: "CHF 50.-",
    detail: "Dépose & pose des 4 roues + ajustement de la pression.",
    note: "Équilibrage (contrôle/correction) : CHF 20.- · Montage de pneus jusqu'à 24 pouces — grille par diamètre sur la page Services",
    featured: false,
  },
];


export default function Home() {
  useSEO(
    "BM MECHANICS — Mécanicien Auto, Moto & Scooter à Prilly (Lausanne)",
    "Atelier de mécanique indépendant à Prilly, Ouest lausannois. Entretien, freinage, diagnostic et réparation auto, moto, scooter. Devis CHF 30.-, déduit des travaux."
  );

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const crestY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const [contenu, setContenu] = useState({ avis: [], settings: {} });
  const [avisForm, setAvisForm] = useState(null);
  const [avisSent, setAvisSent] = useState(false);
  const submitAvis = async (e) => {
    e.preventDefault();
    if (!avisForm.auteur.trim() || !avisForm.texte.trim()) {
      toast.error("Nom et avis requis");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/avis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auteur: avisForm.auteur, texte: avisForm.texte, note: avisForm.note, source: "Site web" }),
      });
      if (!res.ok) throw new Error();
      setAvisSent(true);
      toast.success("Merci ! Votre avis sera publié après validation par l'atelier.");
    } catch {
      toast.error("Échec de l'envoi — réessayez");
    }
  };
  const [firstPair, setFirstPair] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/contenu`)
      .then((r) => r.json())
      .then((d) => d && setContenu(d))
      .catch(() => {});
    fetch(`${API_URL}/photos`)
      .then((r) => r.json())
      .then((d) => {
        if (!Array.isArray(d)) return;
        const afters = d.filter((p) => p.role === "after");
        const b = d.find((p) => p.role === "before" && afters.some((a) => a.pair_id === p.pair_id));
        if (b) {
          const a = afters.find((x) => x.pair_id === b.pair_id);
          setFirstPair({ before: b.data, after: a.data, caption: b.caption });
        }
      })
      .catch(() => {});
  }, []);
  const realAvis = contenu.avis || [];
  const googleReviewUrl = contenu.settings?.google_review_url || SITE.mapsHref;
  const ytEmbed = toYoutubeEmbed(contenu.settings?.video_url || "");
  const promoText = (contenu.settings?.promo_text || "").trim();

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative min-h-[100svh] bg-[#121212] text-[#F6F3EB] overflow-hidden flex items-end texture-grain">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="La voiture vitrine de BM MECHANICS"
            className="h-full w-full object-cover object-[30%_68%] opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/50 to-[#121212]/25" />
        </motion.div>

        <motion.div
          style={{ y: crestY }}
          className="absolute right-6 top-24 md:right-16 md:top-32 w-28 md:w-44 opacity-90 hidden sm:block"
        >
          <div className="border border-[#33353B] p-2 bg-[#F6F3EB] shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <img src="/logo.png" alt="Écusson BM MECHANICS" className="w-full h-auto" />
          </div>
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="origin-left flex items-center gap-4 mb-6"
          >
            <span className="h-px w-10 bg-[#8C7B5D]" />
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#8C7B5D]">
              Atelier indépendant — Prilly · Ouest lausannois
            </p>
          </motion.div>

          <h1 className="font-display font-black uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            <MaskedLines
              lines={[
                "Votre mécanicien",
                "de confiance",
                <span key="l3" className="text-[#8C7B5D]">à Prilly</span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-[#C9C5B8]"
          >
            Mécanique automobile, moto et scooter au cœur de l'Ouest lausannois.
            Entretien, freinage, diagnostic et réparation — avec un devis clair
            avant chaque intervention. Sans surprise, sans jargon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/devis"
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 bg-[#8C7B5D] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] font-medium px-6 py-4 hover:bg-[#F6F3EB] transition-colors"
            >
              Demander un devis <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 border border-[#4A4D53] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
            >
              <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
            data-testid="hero-trust-chips"
          >
            {["Devis CHF 30.- déduit si travaux", "Transparence totale", "Atelier indépendant", "Prilly · VD"].map((chip) => (
              <span key={chip} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C] flex items-center gap-2">
                <span className="h-1 w-1 bg-[#8C7B5D]" /> {chip}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <SectionNav
        items={[
          { id: "offre-hiver", label: "Offre hiver" },
          { id: "atelier", label: "L'atelier" },
          { id: "services", label: "Services" },
          { id: "realisations", label: "Réalisations" },
          { id: "avis", label: "Avis clients" },
        ]}
      />

      {/* ---------------- OFFRE DU MOMENT (banderole défilante, pilotée depuis l'espace atelier) ---------------- */}
      {promoText && (
        <button
          data-testid="promo-banner"
          onClick={() => {
            const el = document.getElementById("offre-hiver");
            const headerH = document.querySelector("header")?.offsetHeight || 80;
            if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -(headerH + 56) });
            else if (el) {
              const y = el.getBoundingClientRect().top + window.scrollY - headerH - 56;
              window.scrollTo({ top: y, behavior: "smooth" });
            }
          }}
          className="block w-full bg-[#0B1924] border-y border-[#1E324A] cursor-pointer hover:bg-[#12263c] transition-colors group"
          aria-label="Voir l'offre spéciale hiver"
        >
          <div className="animate-bm-marquee flex whitespace-nowrap w-max items-center" style={{ animationDuration: "48s" }}>
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center">
                {[...Array(3)].map((_, i) => (
                  <span key={i} className="flex items-center gap-5 px-10 py-3">
                    <img
                      src="/offre-hiver-banner.jpg"
                      alt=""
                      className="h-12 w-auto border border-[#1E324A]"
                      loading="lazy"
                    />
                    <span className="font-display text-lg sm:text-xl font-black uppercase tracking-wide text-[#E6EDF5]">
                      Offre spéciale Hiver
                    </span>
                    <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#8FC3EE]">
                      {promoText}
                    </span>
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#0B1924] bg-[#8FC3EE] px-4 py-2 group-hover:bg-[#E6EDF5] transition-colors">
                      Voir le détail <ArrowDown className="h-3.5 w-3.5" />
                    </span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </button>
      )}

      {/* ---------------- MARQUEE ---------------- */}
      <div className="bg-[#121212] border-t border-[#242529] py-4 overflow-hidden" data-testid="seo-marquee">
        <div className="animate-bm-marquee flex whitespace-nowrap w-max">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${dup}-${item}`} className="flex items-center gap-6 px-6 font-mono text-[11px] uppercase tracking-[0.28em] text-[#9A968C]">
                  {item} <WrenchIcon className="h-3.5 w-3.5 text-[#8C7B5D]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- OFFRE HIVER ---------------- */}
      <section id="offre-hiver" className="py-16 md:py-24 bg-[#EFECE4] hairline-b" data-testid="home-offre-hiver">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="max-w-md mx-auto">
              <img
                src="/offre-hiver.jpg"
                alt="Offre spéciale hiver BM MECHANICS — Pack Hiver CHF 99.- check-up et permutation des 4 roues"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div>
            <SectionHead eyebrow="Offre spéciale hiver" title="Pack Hiver — CHF 99.-" />
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base leading-relaxed text-[#4A4D53]">
                Préparez votre véhicule avant les premiers froids : check-up hiver complet
                (pneus, freins, batterie, éclairage, liquides, essuie-glaces, niveaux)
                + permutation des 4 roues — <span className="font-medium text-[#121212]">CHF 99.- au lieu de CHF 109.-</span>.
              </p>
              <div className="mt-6 space-y-3" data-testid="offres-hiver-details">
                {OFFRES_HIVER.map((o, i) => (
                  <div
                    key={o.name}
                    data-testid={`offre-hiver-detail-${i}`}
                    className={`border p-5 ${
                      o.featured
                        ? "bg-[#121212] text-[#F6F3EB] border-[#121212]"
                        : "bg-white border-[#D8D4CA]"
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-display font-bold uppercase tracking-wide">{o.name}</p>
                      <span className="font-display text-lg font-black text-[#8C7B5D]">{o.price}</span>
                    </div>
                    <p className={`mt-2 text-xs sm:text-sm leading-relaxed ${o.featured ? "text-[#C9C5B8]" : "text-[#4A4D53]"}`}>
                      {o.detail}
                    </p>
                    {o.note && (
                      <p className={`mt-2 font-mono text-[10px] uppercase tracking-[0.14em] ${o.featured ? "text-[#8C7B5D]" : "text-[#9A968C]"}`}>
                        {o.note}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="offre-hiver-rdv-btn"
                  className="inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
                >
                  Prendre rendez-vous <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a
                  href={SITE.phoneHref}
                  data-testid="offre-hiver-call-btn"
                  className="inline-flex items-center gap-2 border border-[#121212] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#121212] hover:text-[#F6F3EB] transition-colors"
                >
                  <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- PRESENTATION ---------------- */}
      <section id="atelier" className="py-16 md:py-24" data-testid="home-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <SectionHead index="01" eyebrow="L'atelier" title="La mécanique comme on l'aime : honnête et bien faite" />
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base leading-relaxed text-[#4A4D53]">
                BM MECHANICS est un atelier de mécanique indépendant installé à Prilly,
                dans l'Ouest lausannois. Nous entretenons et réparons les voitures,
                motos et scooters de toutes marques, avec une conviction simple :
                un client satisfait est un client fidèle — et notre plus belle recommandation.
              </p>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#4A4D53]">
                Ici, pas de facture surprise ni de réparation inutile. Vous repartez
                en sachant exactement ce qui a été fait, pourquoi, et ce qu'il faudra
                prévoir ensuite.
              </p>
              <Link
                to="/a-propos"
                data-testid="intro-about-link"
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#121212] border-b-2 border-[#121212] pb-1 hover:text-[#8C7B5D] hover:border-[#8C7B5D] transition-colors"
              >
                Découvrir l'atelier <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-[#D8D4CA] p-2 bg-white shadow-[0_24px_60px_rgba(18,18,18,0.08)]">
              <img
                src={IMAGES.atelierReel}
                alt="Vue sur l'atelier BM MECHANICS et son enseigne M lumineuse"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">
              Le vrai atelier — Prilly, Canton de Vaud
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------- SERVICES ---------------- */}
      <section id="services" className="py-16 md:py-24 bg-[#EFECE4] hairline-t hairline-b" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHead index="02" eyebrow="Nos services" title="Auto · Moto · Scooter" />
            <Reveal delay={0.1}>
              <Link
                to="/services"
                data-testid="services-all-link"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#121212] border-b-2 border-[#121212] pb-1 hover:text-[#8C7B5D] hover:border-[#8C7B5D] transition-colors mb-10 md:mb-14"
              >
                Tous les services <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SERVICES_HOME.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <article data-testid={s.testid} className="group bg-white border border-[#D8D4CA] hover:border-[#121212] transition-colors h-full flex flex-col">
                  <div className="relative overflow-hidden">
                    <img
                      src={s.image}
                      alt={`Service ${s.title} à Prilly`}
                      className="w-full aspect-[16/10] object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      loading="lazy"
                    />
                    <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center bg-[#121212] text-[#8C7B5D]">
                      <s.icon className="h-5 w-5" />
                    </span>
                    {s.tag && (
                      <span className="absolute left-4 bottom-4 bg-[#121212]/85 text-[#F6F3EB] font-mono text-[10px] uppercase tracking-[0.16em] px-3 py-1.5">
                        {s.tag}
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl font-bold uppercase tracking-wide text-[#121212]">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#4A4D53]">{s.desc}</p>
                    <ul className="mt-4 space-y-2">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-center gap-2 text-sm text-[#4A4D53]">
                          <span className="h-1 w-3 bg-[#8C7B5D]" /> {p}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/devis"
                      data-testid={`${s.testid}-cta`}
                      className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#121212] group-hover:text-[#8C7B5D] transition-colors pt-4 border-t border-[#EFECE4] mt-auto"
                    >
                      Demander un devis <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- AVANT / APRES (visible dès qu'une vraie paire est publiée) ---------------- */}
      {firstPair && (
      <section id="realisations" className="py-16 md:py-24" data-testid="home-beforeafter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <BeforeAfter
              before={firstPair.before}
              after={firstPair.after}
              labelBefore="Avant"
              labelAfter="Après"
              testid="home-before-after-slider"
            />
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">
              {firstPair.caption || "Réalisation BM MECHANICS"}
            </p>
          </Reveal>
          <div>
            <SectionHead index="03" eyebrow="Réalisations" title="Le résultat parle de lui-même" />
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base leading-relaxed text-[#4A4D53]">
                Faites glisser le curseur pour comparer l'avant et l'après d'une
                vraie intervention de l'atelier. La galerie complète vous attend
                sur la page Réalisations.
              </p>
              <Link
                to="/realisations"
                data-testid="home-realisations-link"
                className="mt-6 inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
              >
                Voir les réalisations <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
      )}

      {/* ---------------- VIDEO ATELIER (si lien ajouté dans l'espace atelier) ---------------- */}
      {ytEmbed && (
        <section className="py-16 md:py-24 bg-[#EFECE4] hairline-t hairline-b" data-testid="home-video">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead eyebrow="En vidéo" title="Visitez l'atelier" />
            <Reveal>
              <div className="border border-[#D8D4CA] bg-white p-2 shadow-[0_24px_60px_rgba(18,18,18,0.08)]">
                <div className="aspect-video w-full">
                  <iframe
                    src={ytEmbed}
                    title="Vidéo de présentation de l'atelier BM MECHANICS"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ---------------- TRANSPARENCE ---------------- */}
      <section className="py-16 md:py-24 bg-[#121212] text-[#F6F3EB] texture-grain" data-testid="home-transparence">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="04" eyebrow="Notre engagement" title="Transparence, du devis à la facture" dark />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#242529] border border-[#242529]">
            {TRANSPARENCE.map((t, i) => (
              <Reveal key={t.num} delay={i * 0.08} className="h-full">
                <div data-testid={`transparence-${t.num}`} className="bg-[#121212] p-7 h-full">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-[#8C7B5D] tracking-[0.2em]">{t.num}</span>
                    <t.icon className="h-5 w-5 text-[#4A4D53]" />
                  </div>
                  <h3 className="mt-6 font-display text-lg font-bold uppercase tracking-wide">{t.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#9A968C]">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- POURQUOI ---------------- */}
      <section className="py-16 md:py-24" data-testid="home-pourquoi">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="05" eyebrow="Pourquoi BM Mechanics" title="Un garage indépendant, une parole donnée" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {POURQUOI.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="h-full">
                <div data-testid={`pourquoi-${i}`} className="border border-[#D8D4CA] bg-white p-7 h-full hover:border-[#121212] transition-colors">
                  <p.icon className="h-6 w-6 text-[#8C7B5D]" />
                  <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-[#121212]">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A4D53]">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- AVIS ---------------- */}
      <section id="avis" className="py-16 md:py-24 bg-[#EFECE4] hairline-t hairline-b" data-testid="home-avis">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="06" eyebrow="Avis clients" title="La parole aux clients" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {realAvis.length > 0
              ? realAvis.map((a, i) => (
                  <Reveal key={a.id} delay={i * 0.08} className="h-full">
                    <div data-testid={`avis-real-${i}`} className="h-full border border-[#D8D4CA] bg-white p-7 flex flex-col">
                      <div className="flex gap-1 text-[#8C7B5D]">
                        {[...Array(a.note)].map((_, s) => <Star key={s} className="h-4 w-4 fill-current" />)}
                      </div>
                      <p className="mt-5 text-sm leading-relaxed text-[#4A4D53]">« {a.texte} »</p>
                      {a.reponse && (
                        <div data-testid={`avis-reponse-${i}`} className="mt-4 border-l-2 border-[#8C7B5D] bg-[#F6F3EB] px-4 py-3">
                          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C7B5D]">Réponse de l'atelier</p>
                          <p className="mt-1.5 text-xs leading-relaxed text-[#4A4D53]">{a.reponse}</p>
                        </div>
                      )}
                      <p className="mt-auto pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">
                        {a.auteur} · {a.source}
                      </p>
                    </div>
                  </Reveal>
                ))
              : [1, 2, 3].map((n) => (
                  <Reveal key={n} delay={n * 0.08} className="h-full">
                    <div
                      data-testid={`avis-placeholder-${n}`}
                      className="h-full border border-dashed border-[#B7B2A4] bg-white/50 p-7 flex flex-col"
                    >
                      <div className="flex gap-1 text-[#D8D4CA]">
                        {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4" />)}
                      </div>
                      <p className="mt-5 text-sm leading-relaxed text-[#9A968C] italic">
                        Emplacement réservé à un avis client réel — Google, Facebook ou bouche-à-oreille.
                      </p>
                      <p className="mt-auto pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B7B2A4]">
                        Avis client · {String(n).padStart(2, "0")}
                      </p>
                    </div>
                  </Reveal>
                ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm text-[#4A4D53] max-w-2xl">
              Client de l'atelier ? Votre avis compte : déposez-le ici, il sera publié après
              validation par l'atelier.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                data-testid="avis-form-open-btn"
                onClick={() => { setAvisSent(false); setAvisForm({ auteur: "", texte: "", note: 5 }); }}
                className="inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-3.5 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
              >
                <Star className="h-4 w-4" /> Laisser un avis
              </button>
              <a
                href={googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="avis-google-btn"
                className="inline-flex items-center gap-2 border border-[#121212] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] px-6 py-3.5 hover:bg-[#121212] hover:text-[#F6F3EB] transition-colors"
              >
                <Star className="h-4 w-4" /> Laisser un avis Google
              </a>
            </div>
            {avisSent && (
              <p data-testid="avis-form-success" className="mt-6 bg-white border border-[#D8D4CA] p-5 text-sm text-[#121212] max-w-xl">
                Merci ! Votre avis a bien été transmis — il sera publié après validation par l'atelier.
              </p>
            )}
            {avisForm && !avisSent && (
              <form onSubmit={submitAvis} data-testid="avis-form" className="mt-6 bg-white border border-[#D8D4CA] p-6 max-w-xl space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="af-auteur" className="bm-label">Votre nom / initiale *</label>
                    <input id="af-auteur" data-testid="avis-form-name" className="bm-input" placeholder="Ex. Julien M." value={avisForm.auteur} onChange={(e) => setAvisForm((f) => ({ ...f, auteur: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="af-note" className="bm-label">Votre note</label>
                    <select id="af-note" data-testid="avis-form-note" className="bm-input" value={avisForm.note} onChange={(e) => setAvisForm((f) => ({ ...f, note: Number(e.target.value) }))}>
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} / 5</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="af-texte" className="bm-label">Votre avis *</label>
                  <textarea id="af-texte" data-testid="avis-form-text" className="bm-input min-h-[100px] resize-y" placeholder="Racontez votre expérience à l'atelier…" value={avisForm.texte} onChange={(e) => setAvisForm((f) => ({ ...f, texte: e.target.value }))} />
                </div>
                <button type="submit" data-testid="avis-form-submit" className="w-full sm:w-auto bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-3.5 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors">
                  Envoyer mon avis
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* ---------------- ZONE / CTA FINAL ---------------- */}
      <section className="py-16 md:py-24 bg-[#121212] text-[#F6F3EB] texture-grain" data-testid="home-cta-final">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">
              Prilly · Lausanne · Renens · Chavannes · Ouest lausannois
            </p>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight leading-[0.95]">
              Un voyant allumé ? Un entretien à prévoir ?
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-sm sm:text-base leading-relaxed text-[#9A968C]">
              Décrivez votre besoin en deux minutes et recevez une réponse rapide.
              Ou appelez directement l'atelier.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/devis"
                data-testid="cta-final-quote-btn"
                className="inline-flex items-center gap-2 bg-[#8C7B5D] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] font-medium px-7 py-4 hover:bg-[#F6F3EB] transition-colors"
              >
                Demander un devis <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="cta-final-call-btn"
                className="inline-flex items-center gap-2 border border-[#4A4D53] font-mono text-xs uppercase tracking-[0.16em] px-7 py-4 hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
